import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';

// Fallback hardcodeado por si falla la BD
const NAV_ITEMS_DEFAULT = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊', roles: ['gerencia','administracion','produccion','punto_venta'] },
  { path: '/helados', label: 'Inventario Helados', icon: '🍦', roles: ['gerencia','administracion','produccion','punto_venta'] },
  { path: '/envios-helado', label: 'Envíos de Helado', icon: '🚚', roles: ['gerencia','administracion','produccion'] },
  { path: '/inventario', label: 'Inventario General', icon: '📦', roles: ['gerencia','administracion','punto_venta'] },
  { path: '/cuadre', label: 'Cuadre de Inventario', icon: '⚖️', roles: ['gerencia','administracion'] },
  { path: '/envios-general', label: 'Envíos de Productos', icon: '🛍️', roles: ['gerencia','administracion','produccion'] },
  { path: '/produccion', label: 'Inventario Producción', icon: '🏭', roles: ['gerencia','administracion','produccion'] },
  { path: '/caja', label: 'Control de Caja', icon: '💰', roles: ['gerencia','administracion','punto_venta'] },
  { path: '/sobres', label: 'Sobres', icon: '💵', roles: ['gerencia','administracion','punto_venta'] },
  { path: '/reportes', label: 'Reportes', icon: '📈', roles: ['gerencia','administracion'] },
  { path: '/nomina', label: 'Nómina', icon: '👥', roles: ['gerencia','administracion'] },
  { path: '/ventas', label: 'Ventas Aliaddo', icon: '📊', roles: ['gerencia','administracion'] },
  { path: '/ventas-unificadas', label: 'Ventas Unificadas', icon: '📈', roles: ['gerencia','administracion'] },
  { path: '/pos', label: 'POS Emergencia', icon: '🖥️', roles: ['gerencia','administracion','punto_venta'] },
  { path: '/conciliacion', label: 'Conciliación Bancaria', icon: '🏦', roles: ['gerencia','administracion'] },
  { path: '/cuadre-medios', label: 'Cuadre Medios Electrónicos', icon: '💳', roles: ['gerencia','administracion'] },
  { path: '/tickets', label: 'Novedades', icon: '🔧', roles: ['gerencia','administracion','produccion','punto_venta','empleado'] },
  { path: '/chat', label: 'Chat', icon: '💬', roles: ['gerencia','administracion','produccion','punto_venta','empleado'] },
  { path: '/usuarios', label: 'Usuarios', icon: '👤', roles: ['gerencia','administracion'] },
  { path: '/marcaciones', label: 'Marcaciones', icon: '⏱️', roles: ['gerencia','administracion','empleado'] },
  { path: '/horarios', label: 'Mi Horario', icon: '🗓️', roles: ['empleado'] },
  { path: '/materia-prima', label: 'Materia Prima', icon: '📦', roles: ['gerencia','administracion','produccion'] },
  { path: '/produccion-helados', label: 'Producción Helados', icon: '🍦', roles: ['gerencia','administracion','produccion'] },
  { path: '/proveedores', label: 'Proveedores', icon: '🏭', roles: ['gerencia','administracion'] },
  { path: '/contable', label: 'Módulo Contable', icon: '📒', roles: ['gerencia','administracion'] },
  { path: '/permisos', label: 'Permisos', icon: '🔐', roles: ['gerencia'] },
];

const SIDEBAR_BG = 'rgb(72, 107, 155)';
const SIDEBAR_COLOR = '#ffffff';
const SIDEBAR_ACTIVE_BG = 'rgba(255,255,255,0.2)';
const SIDEBAR_ACTIVE_BORDER = '#ffffff';
const SIDEBAR_MUTED = 'rgba(255,255,255,0.6)';

// ── Campanita de notificaciones ─────────────────────────────
function CampanitaNotificaciones({ fixed }) {
  const [abierto, setAbierto] = useState(false);
  const [notis, setNotis] = useState([]);
  const [cargando, setCargando] = useState(false);
  const navigate = useNavigate();

  const noLeidas = notis.filter(n => !n.leida).length;
  const [expandidas, setExpandidas] = useState(new Set());
  const toggleExpandir = (id, e) => {
    e.stopPropagation();
    setExpandidas(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const cargar = () => {
    API.get('/app/notificaciones').then(r => setNotis(r.data)).catch(() => {});
  };

  useEffect(() => {
    cargar();
    // Refresca cada 60s para que el conteo no quede desactualizado
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, []);

  const abrir = () => {
    setAbierto(o => !o);
    if (!abierto) cargar();
  };

  const marcarLeida = (n) => {
    if (!n.leida) {
      API.put(`/app/notificaciones/${n.id}/leer`).catch(() => {});
      setNotis(prev => prev.map(x => x.id === n.id ? { ...x, leida: true } : x));
    }
    if (!n.url) return;
    setAbierto(false);
    if (n.url.startsWith('/api/')) {
      // Endpoint que devuelve un archivo (ej. el PDF de reposición) — se
      // trae con la autenticación normal (axios ya manda el token) y se
      // abre en pestaña nueva, en vez de navegar dentro de la app (donde
      // no llevaría el token y fallaría).
      const rutaRelativa = n.url.replace(/^\/api/, '');
      API.get(rutaRelativa, { responseType: 'blob' })
        .then(r => {
          const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
          window.open(url, '_blank');
        })
        .catch(() => toast.error('No se pudo abrir el archivo'));
    } else {
      navigate(n.url);
    }
  };

  const marcarTodas = () => {
    setCargando(true);
    API.put('/app/notificaciones/leer-todas')
      .then(() => setNotis(prev => prev.map(x => ({ ...x, leida: true }))))
      .catch(() => toast.error('Error al marcar como leídas'))
      .finally(() => setCargando(false));
  };

  const tiempoRelativo = (iso) => {
    const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `hace ${diffH} h`;
    return `hace ${Math.round(diffH / 24)} d`;
  };

  return (
    <div style={fixed ? { position: 'fixed', top: 20, right: 28, zIndex: 150 } : { position: 'relative' }}>
      <button onClick={abrir} style={{
        position: 'relative', width: 38, height: 38, borderRadius: '50%', border: 'none',
        background: fixed ? 'white' : 'rgba(255,255,255,0.3)',
        boxShadow: fixed ? '0 2px 10px rgba(0,0,0,0.15)' : 'none',
        cursor: 'pointer', fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        🔔
        {noLeidas > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, borderRadius: 8,
            background: '#EF4444', color: 'white', fontSize: 10, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
            border: '2px solid white',
          }}>
            {noLeidas > 9 ? '9+' : noLeidas}
          </span>
        )}
      </button>

      {abierto && (
        <>
          <div onClick={() => setAbierto(false)} style={{ position: 'fixed', inset: 0, zIndex: 149 }} />
          <div style={{
            position: 'absolute', top: 46, right: 0, width: 340, maxHeight: 440, overflowY: 'auto',
            background: 'white', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.2)', zIndex: 150,
          }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>Notificaciones</span>
              {noLeidas > 0 && (
                <button onClick={marcarTodas} disabled={cargando} style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Marcar todas
                </button>
              )}
            </div>
            {notis.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                Sin notificaciones
              </div>
            ) : notis.map(n => {
              const LARGO_MAX = 140;
              const esLargo = n.cuerpo.length > LARGO_MAX;
              const expandida = expandidas.has(n.id);
              const primeraLinea = n.cuerpo.split('\n')[0];
              const cuerpoMostrado = expandida || !esLargo ? n.cuerpo : primeraLinea.slice(0, LARGO_MAX) + '…';
              return (
              <div key={n.id} onClick={() => marcarLeida(n)} style={{
                padding: '12px 16px', borderBottom: '1px solid #F9FAFB', cursor: n.url ? 'pointer' : 'default',
                background: n.leida ? 'white' : '#EFF6FF',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{n.titulo}</span>
                  {!n.leida && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563EB', flexShrink: 0, marginTop: 4 }} />}
                </div>
                <div style={{
                  fontSize: 12, color: '#6B7280', marginTop: 3, whiteSpace: 'pre-line',
                  maxHeight: expandida ? 260 : 'none', overflowY: expandida ? 'auto' : 'visible',
                }}>{cuerpoMostrado}</div>
                {esLargo && (
                  <button onClick={(e) => toggleExpandir(n.id, e)} style={{
                    background: 'none', border: 'none', color: '#2563EB', fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', padding: '4px 0 0', display: 'block',
                  }}>
                    {expandida ? 'Ver menos ▲' : 'Ver todo ▼'}
                  </button>
                )}
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5 }}>{tiempoRelativo(n.created_at)}</div>
              </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { usuario, logout, puedeVer } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState(NAV_ITEMS_DEFAULT);

  // Cargar permisos desde BD al montar
  useEffect(() => {
    API.get('/modulos/permisos')
      .then(r => {
        const items = r.data.map(m => ({
          path: m.path,
          label: m.nombre,
          icon: m.icon,
          roles: m.roles,
        }));
        // Agregar Permisos (solo gerencia, siempre hardcodeado)
        items.push({ path: '/permisos', label: 'Permisos', icon: '🔐', roles: ['gerencia'] });
        setNavItems(items);
      })
      .catch(() => {}); // Si falla usa el fallback hardcodeado
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const itemsFiltrados = navItems.filter(item => puedeVer(...item.roles));

  const LABEL_ROLES = {
    gerencia: 'Gerencia',
    administracion: 'Administración',
    produccion: 'Producción',
    punto_venta: usuario?.area_nombre || 'Punto de Venta',
    empleado: 'Empleado',
  };

  const currentItem = itemsFiltrados.find(i => location.pathname.startsWith(i.path));

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .erp-sidebar { display: none !important; }
          .erp-main { margin-left: 0 !important; padding-bottom: 70px !important; }
          .erp-topbar { display: flex !important; }
          .erp-bottomnav { display: flex !important; }
          .erp-drawer { display: flex !important; }
          .erp-bell-desktop { display: none !important; }
        }
        @media (min-width: 769px) {
          .erp-sidebar { display: flex !important; }
          .erp-main { margin-left: 240px !important; }
          .erp-topbar { display: none !important; }
          .erp-bottomnav { display: none !important; }
          .erp-drawer { display: none !important; }
          .erp-bell-desktop { display: block !important; }
        }
        .erp-navitem:hover { background: rgba(255,255,255,0.2) !important; }
      `}</style>

      <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif", background: 'rgb(248, 240, 227)' }}>

        {/* ── SIDEBAR DESKTOP ── */}
        <aside className="erp-sidebar" style={{
          width: 240, background: SIDEBAR_BG, flexDirection: 'column',
          position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100,
          boxShadow: '2px 0 12px rgba(0,0,0,0.15)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '22px 20px', borderBottom: '1px solid rgba(255,255,255,0.3)' }}>
            <img src="data:image/png;base64,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" alt="Gelatus" style={{ height: 32, objectFit: 'contain' }} />
            <div>
              <div style={{ color: SIDEBAR_COLOR, fontWeight: 800, fontSize: 15 }}>Latu</div>
              <div style={{ color: SIDEBAR_MUTED, fontSize: 11 }}>Gelatus</div>
            </div>
          </div>

          <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
            {itemsFiltrados.map((item) => {
              const active = location.pathname.startsWith(item.path);
              return (
                <Link key={item.path} to={item.path} className="erp-navitem" style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 20px', textDecoration: 'none', fontSize: 14,
                  color: active ? SIDEBAR_COLOR : SIDEBAR_MUTED,
                  background: active ? SIDEBAR_ACTIVE_BG : 'transparent',
                  borderLeft: `3px solid ${active ? SIDEBAR_ACTIVE_BORDER : 'transparent'}`,
                  fontWeight: active ? 700 : 500,
                  transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: 18, width: 22, textAlign: 'center' }}>{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: SIDEBAR_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                {usuario?.nombre?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ color: SIDEBAR_COLOR, fontSize: 13, fontWeight: 700 }}>{usuario?.nombre}</div>
                <div style={{ color: SIDEBAR_MUTED, fontSize: 11 }}>{LABEL_ROLES[usuario?.rol]}</div>
              </div>
            </div>
            <button onClick={handleLogout} style={{ width: '100%', padding: 8, background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8, color: SIDEBAR_COLOR, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              ⬅ Cerrar sesión
            </button>
          </div>
        </aside>

        {/* ── CAMPANITA DESKTOP ── */}
        <div className="erp-bell-desktop">
          <CampanitaNotificaciones fixed />
        </div>

        {/* ── TOPBAR MÓVIL ── */}
        <div className="erp-topbar" style={{
          display: 'none', position: 'fixed', top: 0, left: 0, right: 0, height: 54,
          background: SIDEBAR_BG, zIndex: 200, alignItems: 'center',
          padding: '0 16px', gap: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <img src="data:image/png;base64,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" alt="Gelatus" style={{ height: 26, objectFit: 'contain' }} />
          <div style={{ flex: 1, color: SIDEBAR_COLOR, fontWeight: 800, fontSize: 15 }}>
            {currentItem ? `${currentItem.icon} ${currentItem.label}` : 'Latu'}
          </div>
          <CampanitaNotificaciones />
          <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.3)', border: 'none', borderRadius: 8, padding: '6px 12px', color: SIDEBAR_COLOR, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            Salir
          </button>
        </div>

        {/* ── MAIN ── */}
        <main className="erp-main" style={{ flex: 1, minHeight: '100vh' }}>
          <div style={{ padding: '28px', maxWidth: 1400, paddingTop: 28 }}
            className="erp-content">
            <style>{`
              @media (max-width: 768px) {
                .erp-content { padding: 70px 14px 20px !important; }
              }
            `}</style>
            {children}
          </div>
        </main>

        {/* ── BOTTOM NAV MÓVIL ── */}
        <nav className="erp-bottomnav" style={{
          display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
          background: SIDEBAR_BG, borderTop: '1px solid rgba(255,255,255,0.4)',
          zIndex: 200, alignItems: 'stretch', boxShadow: '0 -2px 10px rgba(0,0,0,0.12)',
        }}>
          {/* Mostrar máximo 4 items + botón "más" */}
          {itemsFiltrados.slice(0, 4).map((item) => {
            const active = location.pathname.startsWith(item.path);
            return (
              <Link key={item.path} to={item.path} style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '8px 4px', textDecoration: 'none',
                color: active ? SIDEBAR_COLOR : SIDEBAR_MUTED,
                background: active ? SIDEBAR_ACTIVE_BG : 'transparent',
                borderTop: `2px solid ${active ? SIDEBAR_ACTIVE_BORDER : 'transparent'}`,
                gap: 2,
              }}>
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <span style={{ fontSize: 9, fontWeight: active ? 700 : 500, textAlign: 'center', lineHeight: 1.1 }}>
                  {item.label.split(' ')[0]}
                </span>
              </Link>
            );
          })}
          {itemsFiltrados.length > 4 && (
            <button onClick={() => setMenuOpen(true)} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: '8px 4px', background: 'none',
              border: 'none', cursor: 'pointer', color: SIDEBAR_MUTED, gap: 2,
            }}>
              <span style={{ fontSize: 20 }}>☰</span>
              <span style={{ fontSize: 9, fontWeight: 500 }}>Más</span>
            </button>
          )}
        </nav>

        {/* ── DRAWER MÓVIL (menú completo) ── */}
        {menuOpen && (
          <div className="erp-drawer" style={{
            display: 'none', position: 'fixed', inset: 0, zIndex: 300,
          }}>
            <div onClick={() => setMenuOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: SIDEBAR_BG, borderRadius: '16px 16px 0 0',
              padding: '20px 16px 32px', maxHeight: '80vh', overflowY: 'auto',
            }}>
              <div style={{ width: 36, height: 4, background: 'rgba(255,255,255,0.4)', borderRadius: 2, margin: '0 auto 20px' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '0 4px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: SIDEBAR_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 16 }}>
                  {usuario?.nombre?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ color: SIDEBAR_COLOR, fontWeight: 700, fontSize: 14 }}>{usuario?.nombre}</div>
                  <div style={{ color: SIDEBAR_MUTED, fontSize: 12 }}>{LABEL_ROLES[usuario?.rol]}</div>
                </div>
              </div>
              {itemsFiltrados.map((item) => {
                const active = location.pathname.startsWith(item.path);
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMenuOpen(false)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px',
                    textDecoration: 'none', borderRadius: 10, marginBottom: 4,
                    background: active ? SIDEBAR_ACTIVE_BG : 'transparent',
                    color: active ? SIDEBAR_COLOR : SIDEBAR_MUTED,
                    fontWeight: active ? 700 : 500, fontSize: 15,
                  }}>
                    <span style={{ fontSize: 22 }}>{item.icon}</span>
                    {item.label}
                  </Link>
                );
              })}
              <button onClick={handleLogout} style={{ width: '100%', marginTop: 12, padding: '12px', background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 10, color: SIDEBAR_COLOR, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                ⬅ Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
